import {ref, reactive, computed, toRefs, nextTick} from 'vue';
import {usePageResize} from "@/modules/reader/book/pageResize";
import {useBookInfo} from "@/modules/reader/book/bookinfo";
import {usePaging} from "@/modules/reader/book/paging";
import {useMuteRoleControl} from "@/modules/reader/karaoke/muteRoleControl";
import {isTouchDevice, isIOS} from "@/modules/utils/deviceUtils";
import config from "@/config/config.json";
import {replaceAudioPathForStagingServer} from  "@/modules/utils/pathUtils";

const karaokeState = reactive({
    currentAudioId:null,
    activeGrouptag:null,
    activeAudioSrc:null,
    currentParagraphRowElWidth:[],
    wholePageData:[],
    wordAudioData:null,
    rowFirstWordTimePos:[],
    currentRowIndex:0,
    currentWordIndex:0,
    wholePageActiveParaIndex:0,
    activePageNum:null,
    multiWholePageStatus:{}, //for storing reactive state in multiple whole page mode
    multiplePageParaStatus:{}, //for storing reactive state in multiple page by paragraph mode
    btnIdPageMapping:null,
    btnId:null,
    lastWordTime:null,
    isMuted:false
});

const MODE_WHOLE_PAGE = "wholePage";
const MODE_MULTIPLE_WHOLE_PAGE = "multipleWholePage";
const MODE_MULTIPLE_PAGE_PARA = "multiplePageByParagraph";
const MODE_SINGLE_PARAGRAPH = "singleParagraph";

let audioEl = null;
let accumulatedWidth = 0;
let progress = 0;
let progressOnPaused = 0 //how much is animated when the audio is paused, used to restore the progress on resumed.
let animateId = null;
let wordStartTime = null; //timestamp when each individual word starts playing
let playingTimeStamp = null //timestamp while animation is playing
let initialDelay = 0;
let initialTimeoutId = null //timeout id or the starting delay

export function useKaraokeControl() {

    //const { useBookInfo().bookInfoState} = useBookInfo();
    const {pageResizeState} = usePageResize();
    const {muteRoleState} = useMuteRoleControl();

    const playParagraph = (currentWordAudioData)=> {

        console.log('@playParagraph')

        karaokeState.wordAudioData = currentWordAudioData;
        karaokeState.rowFirstWordTimePos = [];

        for (let rowIndex = 0; rowIndex < karaokeState.wordAudioData.length; rowIndex++) {
            karaokeState.currentParagraphRowElWidth.push(0);
            karaokeState.rowFirstWordTimePos.push(karaokeState.wordAudioData[rowIndex][0]["duration"]);
        }

        //attempt to get the time position when the last word of the last row reaches, since the physical duration is not always equal to the manual time data when authorizing karaoke.
        let rowCount = karaokeState.wordAudioData.length;
        let lastRow = karaokeState.wordAudioData[rowCount-1];
        karaokeState.lastWordTime = lastRow[lastRow.length -1]["duration"];

        audioEl = document.getElementById("karaokePlayer");
        //Important : must remove timeupdate listener and set unmute, otherwise, there will be problem in muting audio in iOS safari!
        audioEl.removeEventListener("timeupdate", setAudioMuteState);
        audioEl.muted = false;
        audioEl.addEventListener("timeupdate", setAudioMuteState);
        audioEl.play();
        delayAnimation();
        //audioEl.addEventListener("timeupdate", setAudioMuteState);

    }

    /**
     * There should be a short delay before tha audio starts, according to the karaoke
     */
    const delayAnimation = ()=> {

        initialDelay = karaokeState.wordAudioData[0][karaokeState.currentWordIndex]["duration"] / useBookInfo().bookInfoState.audioPlaybackRate;

        //need more delay for ios...

        if (isIOS()) {
            initialDelay += 500;
        }
        //the first data is always zero increment. So just add setTimeout
        initialTimeoutId = setTimeout(() => {
            wordStartTime = null;
            animateId = window.requestAnimationFrame(animateRow);
        }, initialDelay );
    }

    const setAudioMuteState = (e)=>{
        e.target.muted = muteRoleState.isMuted;
    };

    /**
     * Called when the audio progress slider handler is moved for audio seeking
     * @param currentTime
     * @param isPaused
     */
    const seekAudio = (currentTime, isPaused) => {

        //avoid further error in case activeAudioSrc has no value;
        if (!karaokeState.activeAudioSrc) {
            return;
        }

        //stop the animation frame so that the row won't go playing
        window.cancelAnimationFrame(animateId);

        let updatedCurrentTime = Math.floor(currentTime * 1000);

        //if playhead is pointed to zero, consider it as replay:
        if (currentTime === 0) {

            clearKaraokeAnimation();
            karaokeState.currentParagraphRowElWidth.push(0);
            initialDelay = karaokeState.wordAudioData[0][0]["duration"] / useBookInfo().bookInfoState.audioPlaybackRate;
            audioEl.currentTime = 0;
            audioEl.play();
            //the first data is always zero increment. So just add setTimeout
            setTimeout(() => {
                animateId = window.requestAnimationFrame(animateRow);
            }, initialDelay );

            return;
        }

        //find the target currentRowIndex to seek to. This is done by getting the duration data of the first word of each row and compare with the audio current time
        if (updatedCurrentTime <= karaokeState.rowFirstWordTimePos[0]) {
            karaokeState.currentRowIndex = 0;
        } else {
            for (let i = 0; i < karaokeState.rowFirstWordTimePos.length; i++) {
                if (i === karaokeState.rowFirstWordTimePos.length - 1) {
                    karaokeState.currentRowIndex = i;
                } else if (updatedCurrentTime >= karaokeState.rowFirstWordTimePos[i] && updatedCurrentTime <= karaokeState.rowFirstWordTimePos[i + 1]) {
                    karaokeState.currentRowIndex = i;
                    break;
                }
            }
        }

        let isWordIndexLocated = false;
        //after updating the current row index, locate the target word position by comparing with the audio current time
        for (let j = 1; j < karaokeState.wordAudioData[karaokeState.currentRowIndex].length; j++) {
            if ( karaokeState.wordAudioData[karaokeState.currentRowIndex][j]["duration"] >= updatedCurrentTime) {
                karaokeState.currentWordIndex = j-1;
                isWordIndexLocated = true;
                break;
            }
        }

        //this happens if the current time is sought to the the point between two rows, and delay is set between these two rows. In this case, jump to the next row
        if (!isWordIndexLocated) {
            console.log("not located!! " + currentTime  + " ; karaokeState.currentRowIndex: " +  karaokeState.currentRowIndex)
            if (karaokeState.currentRowIndex <  karaokeState.wordAudioData.length -1) {
                karaokeState.currentRowIndex++;
                karaokeState.currentWordIndex = 0;
                currentTime = karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"] / 1000;
            } else {
                karaokeState.currentWordIndex =  karaokeState.wordAudioData[karaokeState.currentRowIndex].length - 2;
                currentTime = karaokeState.wordAudioData[karaokeState.currentRowIndex].slice(-1).pop()["duration"] / 1000;
            }
        }

        //clear all current paragraph karaoke row width
        for (let item of karaokeState.currentParagraphRowElWidth) {
            item = 0;
        }

        //fill the word length:
        if (karaokeState.currentRowIndex === 0) {
            karaokeState.currentParagraphRowElWidth[karaokeState.currentRowIndex] = Math.round(karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["increment"] * pageResizeState.imgToBrowserScale);
        } else {
            for (let k= 0;  k <=  karaokeState.currentRowIndex; k++) {
                //fill the entire row if the row index is smaller than the target row, since these rows "should have been played" by seeking.
                if (k < karaokeState.currentRowIndex) {
                    karaokeState.currentParagraphRowElWidth[k] = Math.round(karaokeState.wordAudioData[k].slice(-1).pop()["increment"] * pageResizeState.imgToBrowserScale);
                    //otherwise, fill the words up to the target word position
                } else {
                    karaokeState.currentParagraphRowElWidth[k] = Math.round(karaokeState.wordAudioData[k][karaokeState.currentWordIndex]["increment"] * pageResizeState.imgToBrowserScale);
                }
            }
        }

        audioEl.currentTime = currentTime;
        wordStartTime = null;
        accumulatedWidth = parseInt(karaokeState.currentParagraphRowElWidth[karaokeState.currentRowIndex])

        //get the increment so that the audio can sync with the text
        let passedTime = currentTime * 1000 - karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"];
        let duration = karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex+1]["duration"] - karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"]
        let offset =  (karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex+1]["increment"] -  karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["increment"]) * passedTime / duration;
        //console.log("passedtime " + passedTime + " ; " + "duration: " + duration + " ; offset: " + offset);

        accumulatedWidth += offset * pageResizeState.imgToBrowserScale

        if (karaokeState.currentWordIndex === karaokeState.wordAudioData[karaokeState.currentRowIndex].length - 1) {
            onCurrentRowPlayFinished();
            return;
        }

        if (!isPaused) {
            animateId = window.requestAnimationFrame(animateRow);
        }
    }

    /**
     * The main animation cycle
     * @param timestamp
     */
    const animateRow = (timestamp)=> {

        if (!karaokeState.wordAudioData) {
            return;
        }

        if (!wordStartTime) {
           wordStartTime = timestamp;
        }

        playingTimeStamp = timestamp;

        let currentWordExpectedTime =  karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex+1]["duration"];
        //Value in pixel that is expected to increase by the current word
        let increment = (karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex+1]["increment"] - karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["increment"])
        //how much time the current word is needed to play
        let duration = currentWordExpectedTime  - karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"];

        //avoid the last word of the last row not being able to cover... this is due to an extra 0.5 second length in each audio duration.
        //That is, each audio clip is added 0.5 second since the last word could not be covered when building the karaoke data.
        //Consult the authoring team for more details...
        if (karaokeState.currentWordIndex === karaokeState.wordAudioData[karaokeState.currentRowIndex].length - 2 &&  karaokeState.currentRowIndex === karaokeState.wordAudioData.length -1 ) {
            currentWordExpectedTime += 500;
        }

        //it's detected that the audio is somehow a bit faster than the animation. Add accelerateLevel to sync as much as possible...
        //if the duration is larger than slowWordBoundary, it's considered slow word. The majority is "fast word"
        //keep not accelerated for iOS...

        let accelerateLevel = (duration <= config.reader.karaoke.slowWordBoundary &&  !isIOS()) ?  config.reader.karaoke.accelerateLevel : 1

        progress = (timestamp - wordStartTime) / (duration / useBookInfo().bookInfoState.audioPlaybackRate) * accelerateLevel + progressOnPaused;

        //the duration data of two succeeding words could be equal, this would result in progress = NaN since wordStartTime would be reset to null before next word starts. so set progress = 1 in this case:
        if ( karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"] ===  currentWordExpectedTime) {
            progress = 1;
        }

        //normally, the audio current time should be less than the expected current time marked in the audio data, when the current word is being played..
        //but there is still chance that the audio current time is greater than it..(see prichi_3au1tb paragraph 3, 4th row for example).
        //This results in parts of the last word or the whole last word not being covered.. Since the audio current time and the expected current time is small
        //enough, just treat is as being played by setting progress = 1 and jump to the next row.
        if ( audioEl.currentTime * 1000 > currentWordExpectedTime) {
            progress = 1;
        }

        karaokeState.currentParagraphRowElWidth[karaokeState.currentRowIndex] =  Math.ceil(accumulatedWidth + (increment * pageResizeState.imgToBrowserScale * progress))
        //console.log(progress + " ; " + karaokeState.currentRowIndex + " ; " + karaokeState.currentWordIndex + " ; " +  audioEl.currentTime + " ; " + karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex+1]["duration"] + " ; " +  karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"])

        if (progress < 1  && audioEl.currentTime * 1000   < currentWordExpectedTime) {
            animateId = window.requestAnimationFrame(animateRow);
         //otherwise jump to next word / row
        } else {
            progress = 0;
            progressOnPaused = 0;
            accumulatedWidth = karaokeState.currentParagraphRowElWidth[karaokeState.currentRowIndex];
            karaokeState.currentWordIndex++;
            //Jump to next row if all words of the current row has been played
            if (karaokeState.currentWordIndex === karaokeState.wordAudioData[karaokeState.currentRowIndex].length - 1) {
                onCurrentRowPlayFinished();
                window.cancelAnimationFrame(animateId);

                //there should be some delay between each row; the delay data comes from the karaoke data file.
                //It's the difference between the last word duration of a row and the first word duration of the next row:
                if (karaokeState.currentRowIndex > 0) {
                    let previousRowDelay = karaokeState.wordAudioData[karaokeState.currentRowIndex - 1].slice(-1).pop()["duration"]
                    let currentRowDelay = karaokeState.wordAudioData[karaokeState.currentRowIndex][karaokeState.currentWordIndex]["duration"]
                    let delay = currentRowDelay - previousRowDelay
                    setTimeout(() => {
                        wordStartTime = null;
                        animateId = window.requestAnimationFrame(animateRow);
                    }, delay / useBookInfo().bookInfoState.audioPlaybackRate);
                }
                //otherwise just go ahead to play the next word
            } else {
                wordStartTime = null;
                animateId = window.requestAnimationFrame(animateRow);
            }
        }

        //document.getElementById("karaokePlayer").volume = (karaokeState.istimeupdate ) ? 1 : 0;
        //document.getElementById("karaokePlayer").muted = karaokeState.isMuted
    }

    /**
     * Called when one karaoke row is completed. Determine whether the next row or paragraph will be played, or just stop all karaoke
     * Since audio seeking to the end of the control slider should end the karaoke row, so the implementation is handled in this
     * method so that it can be shared.
     */
    const onCurrentRowPlayFinished = () => {

        //if current paragraph finishes playing
        if (karaokeState.currentRowIndex === karaokeState.wordAudioData.length - 1) {

            window.cancelAnimationFrame(animateId);

            if (karaokeState.playMode === MODE_WHOLE_PAGE) {

                //if all paragraphs have finished playing in whole page mode
                if (karaokeState.wholePageActiveParaIndex === karaokeState.wholePageData.length -1 ) {
                    stopAnimation();
                    return;
                } else {
                    clearKaraokeAnimation();
                    setWholePageNextParaData();
                    return;
                }
            } else if (karaokeState.playMode === MODE_MULTIPLE_WHOLE_PAGE) {

                //if the target button is play page karaoke button, check the number of pages played to see if it's complete or not:
                if (karaokeState.multiWholePageStatus.isTargetPlayPageBtn) {

                    //if all paragraphs in the current page have finished playing:
                    if (karaokeState.wholePageActiveParaIndex === karaokeState.wholePageData.length -1 ) {

                        karaokeState.multiWholePageStatus.playedPageCount++;

                        if ( karaokeState.multiWholePageStatus.playedPageCount === karaokeState.multiWholePageStatus.btnIdPageMapping.length) {
                            stopAnimation();
                            return;
                        }

                        karaokeState.multiWholePageStatus.multiPageTargetBtnId = karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedPageCount]["aimPageBtnId"]; //which button id of the target page to point to.
                        karaokeState.multiWholePageStatus.multiPageTargetPageNum = karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedPageCount]["aimPageIndex"]; //which page to point to

                        //reset wholePageActiveParaIndex since it has to be started from zero to check when all paragraphs on the target page will finish
                        karaokeState.wholePageActiveParaIndex = 0;
                        stopCompletedPageAnimation();
                        usePaging().setCurrentPage(karaokeState.multiWholePageStatus.multiPageTargetPageNum, true);
                        return;

                    } else {
                        console.log("@@setWholePageNextParaData")
                        clearKaraokeAnimation();
                        setWholePageNextParaData();
                        return;
                    }
                } else {

                    console.log("MODE_MULTIPLE_WHOLE_PAGE, WITHOUT isTargetPlayPageBtn!!")
                    console.log(karaokeState.multiWholePageStatus.playedParaCount)

                    karaokeState.multiWholePageStatus.playedParaCount++;

                    if (karaokeState.multiWholePageStatus.playedParaCount === karaokeState.multiWholePageStatus.btnIdPageMapping.length) {
                        console.log("finish multi whole page!!");
                        stopAnimation();
                        return;
                    }

                    let nextParagraphIndex = karaokeState.multiWholePageStatus.playedParaCount;

                    //if the target page index (set in btnIdPageMapping.aimPageIndex) is not the same as the current paragraph target page index,
                    //all the paragraphs on the current page should have finished playing, a page turn should be triggered:
                    if (karaokeState.multiWholePageStatus.btnIdPageMapping[nextParagraphIndex]["aimPageIndex"] !== karaokeState.multiWholePageStatus.multiPageTargetPageNum) {
                        karaokeState.multiWholePageStatus.multiPageTargetBtnId = karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedParaCount]["aimPageBtnId"]; //which button id of the target page to point to.
                        karaokeState.multiWholePageStatus.multiPageTargetPageNum = karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedParaCount]["aimPageIndex"]; //which page to point to
                        stopCompletedPageAnimation();
                        usePaging().setCurrentPage(karaokeState.multiWholePageStatus.multiPageTargetPageNum, true);
                        return;

                    } else {
                        clearKaraokeAnimation();
                        karaokeState.multiWholePageStatus.multiPageTargetBtnId =  karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedParaCount]["aimPageBtnId"]; //which button id of the target page to point to. It must be the first item in the mapping to start with
                        karaokeState.multiWholePageStatus.multiPageTargetPageNum = karaokeState.multiWholePageStatus.btnIdPageMapping[karaokeState.multiWholePageStatus.playedParaCount]["aimPageIndex"]; //target page number (zero-based).  It must be the first item in the mapping to start with
                        return;
                    }
                }
            } else if (karaokeState.playMode === MODE_MULTIPLE_PAGE_PARA) {

                console.log("single paragraph finishes playing, ready to target next!");
                console.log(karaokeState.btnIdPageMapping)

                let aimPageIndex =  parseInt(Object.keys(karaokeState.btnIdPageMapping)[0]);
                let aimPageBtnId = Object.values(karaokeState.btnIdPageMapping)[0];

                if (karaokeState.multiplePageParaStatus.aimPageBtnId) {
                    stopAnimation();
                    return;
                }

                stopCompletedPageAnimation();

                if ( karaokeState.activePageNum !==  aimPageIndex) {
                    usePaging().setCurrentPage(aimPageIndex, true);

                }

                karaokeState.multiplePageParaStatus = {
                    aimPageIndex: aimPageIndex,
                    aimPageBtnId: aimPageBtnId
                }

            } else {
                stopAnimation();
                return;
            }

        }  else {
            karaokeState.currentRowIndex++;
            karaokeState.currentWordIndex = 0;
            progress = 0;
            progressOnPaused = 0;
            accumulatedWidth = 0;
            karaokeState.currentParagraphRowElWidth[karaokeState.currentRowIndex] = 0;
        }

    }


    const setCurrentAudioSrc = (audioSrc)=> {

        /*
        if (useBookInfo().bookInfoState.availableSpeech.length > 1) {
            return `${useBookInfo().bookInfoState.publicPath}/${audioSrc.split(".mp3")[0]}_${useBookInfo().bookInfoState.activeSpeech}.mp3`;
        } else {
            return `${useBookInfo().bookInfoState.publicPath}/${audioSrc}`;
        }*/


        let path;

        if (useBookInfo().bookInfoState.availableSpeech.length > 1) {
            path =`${useBookInfo().bookInfoState.publicPath}/${audioSrc.split(".mp3")[0]}_${useBookInfo().bookInfoState.activeSpeech}.mp3`;
        } else {
            path = `${useBookInfo().bookInfoState.publicPath}/${audioSrc}`;
        }

        path = replaceAudioPathForStagingServer(path);
        return path;

    }


    /**
     * Update audio data to play the next paragraph in whole page mode
     */
    const setWholePageNextParaData = ()=> {
        karaokeState.activeGrouptag = null;
        karaokeState.wholePageActiveParaIndex++;
        console.log('@setWholePageNextParaData karaokeState.wholePageActiveParaIndex: ' + karaokeState.wholePageActiveParaIndex);
        karaokeState.currentAudioId = karaokeState.wholePageData[karaokeState.wholePageActiveParaIndex].audioId;
        karaokeState.activeGrouptag = karaokeState.wholePageData[karaokeState.wholePageActiveParaIndex].grouptag
        karaokeState.activeAudioSrc =  setCurrentAudioSrc(karaokeState.wholePageData[karaokeState.wholePageActiveParaIndex].audioSrc);

        nextTick(()=>{
            playParagraph(karaokeState.wholePageData[karaokeState.wholePageActiveParaIndex]["wordAudioData"]);
        })

    }

    /**
     * Clear the animated elements only, without resetting karaoke data
     */
    const clearKaraokeAnimation = ()=> {
        window.cancelAnimationFrame(animateId);
        initialDelay = 0;
        if (initialTimeoutId) {
            clearTimeout(initialTimeoutId);
        }
        animateId = null;
        progress = 0;
        wordStartTime = null;
        playingTimeStamp = null;
        progressOnPaused = 0;
        accumulatedWidth = 0;
        karaokeState.currentParagraphRowElWidth = [];
        karaokeState.currentRowIndex = 0;
        karaokeState.currentWordIndex = 0;
    }

    /**
     * Hard reset karaoke data
     */
    const reset =()=> {
        console.log("@karaokeControl reset")
        clearKaraokeAnimation();
        karaokeState.currentParagraphRowElWidth = [];
        karaokeState.currentRowIndex = 0;
        karaokeState.currentWordIndex = 0;
        karaokeState.currentAudioId = null;
        karaokeState.activeGrouptag = null;
        karaokeState.activeAudioSrc = null;
        karaokeState.wholePageData = [];
        karaokeState.wholePageActiveParaIndex = 0;
        karaokeState.activePageNum = null;
        karaokeState.multiWholePageStatus = {};
        karaokeState.multiplePageParaStatus = {};
        karaokeState.btnId = null;
        karaokeState.btnIdPageMapping = null;
        karaokeState.lastWordTime = null;
        karaokeState.playMode = null;
        karaokeState.wordAudioData = null;
        karaokeState.rowFirstWordTimePos = [];
        muteRoleState.isMuted = false;
    }

    /**
     * Stop the karaoke animation from playing
     */
    const stopAnimation = ()=> {
        if (audioEl) {
            audioEl.removeEventListener("timeupdate", setAudioMuteState);
            audioEl.pause();
            audioEl.currentTime = 0;
        }
        reset();
    }

    /**
     * Used in multiPage mode, stop the karaoke animation and audio for the pages that have completed only,
     * leaving the karaoke data for the next pages
     */
    const stopCompletedPageAnimation = ()=> {
        clearKaraokeAnimation();
        if (audioEl) {
            audioEl.pause();
            audioEl.currentTime = 0;
        }
    }

    const pauseAnimation = ()=> {
        window.cancelAnimationFrame(animateId);
        progressOnPaused = progress;
    }

    const resumeAnimation = ()=> {
        wordStartTime = null;
        //audioEl.currenTime -= playingTimeStamp - wordStartTime
        animateId = window.requestAnimationFrame(animateRow)
    }

    return {
        karaokeState,
        seekAudio,
        clearKaraokeAnimation,
        setCurrentAudioSrc,
        reset,
        playParagraph,
        stopAnimation,
        stopCompletedPageAnimation,
        pauseAnimation,
        resumeAnimation,
        MODE_WHOLE_PAGE,
        MODE_MULTIPLE_WHOLE_PAGE,
        MODE_MULTIPLE_PAGE_PARA,
        MODE_SINGLE_PARAGRAPH
    }
}